import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type { ContactGift } from '@/lib/types'

export function useContactGifts(contactId: string | undefined) {
  return useQuery({
    queryKey: ['gifts', contactId],
    queryFn: () =>
      api<{ items: ContactGift[]; total: number }>(`/api/contacts/${contactId}/gifts`).then(
        (r) => r.items,
      ),
    enabled: !!contactId,
  })
}

export function useCreateGift() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      ...data
    }: {
      contactId: string
      title: string
      url?: string
      price?: string
      notes?: string
    }) =>
      api<ContactGift>(`/api/contacts/${contactId}/gifts`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['gifts', variables.contactId] })
      toast.success(i18n.t('toast.giftCreated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useUpdateGift() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      title?: string
      url?: string
      price?: string
      notes?: string
      purchased?: boolean
      sort_order?: number
    }) => api<ContactGift>(`/api/gifts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gifts'] })
      toast.success(i18n.t('toast.giftUpdated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useDeleteGift() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/gifts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gifts'] })
      toast.success(i18n.t('toast.giftDeleted'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
